/**
 * Brand hub pages: the missing crawl path to the camera catalog.
 *
 * Measured 2026-08-02: of 21,795 camera pages, only 146 were reachable by
 * following an `<a href>` from anywhere on the site. `/cameras` renders exactly
 * 100 links while announcing "22,899 cameras", because its brand filter is a
 * `<button onClick>` and its "load more" is a `<button>` — neither is a link, so
 * Googlebot sees one page of a hundred and stops. Camera pages do link ten
 * same-brand siblings each, but those form ~1,200 closed islands with no edge
 * back to the browsable site. Result: 99.3% of the catalog existed only in the
 * sitemap, and Google indexed roughly half of it.
 *
 * A brand hub fixes that with two hops. `/cameras` links each brand, a brand
 * page links every model it has, so every camera page sits three clicks from
 * the home page along ordinary anchors.
 *
 * It also repairs a live redirect that currently points nowhere:
 * `public/.htaccess:111` sends `/cameras/<brand>` to `/brand/<brand>` with a
 * 301, but no such route existed, so the target answered 200 with the home
 * page's HTML and a canonical on the root — a soft 404 at the end of a
 * permanent redirect.
 */
import { getAllCamerasForStaticPages, type CameraPageData } from '@/lib/cameraSlug';
import { brandHubPath, brandPagePath, generateBrandSlug as toBrandSlug } from '@/lib/catalogRoutes';

export interface BrandSummary {
  /** URL segment, e.g. "hikvision". */
  slug: string;
  /** Display name exactly as the catalog spells it, e.g. "Hikvision". */
  name: string;
  cameraCount: number;
}

/**
 * A brand needs at least this many cameras to get its own page.
 *
 * Was 3, to avoid hubs whose whole content is one link. Lowered to 1 when
 * brands moved to the root: `/hive/<model>` resolving while `/hive` 404s is a
 * worse shape than a short hub, and it left five brands unreachable
 * (360-vision-technology, bosch-smart-home, hive, netcamcenter, steinel).
 */
export const MIN_CAMERAS_FOR_BRAND_PAGE = 1;

export { generateBrandSlug } from '@/lib/catalogRoutes';

let _bySlug: Map<string, { name: string; cameras: CameraPageData[] }> | null = null;

function index(): Map<string, { name: string; cameras: CameraPageData[] }> {
  if (_bySlug) return _bySlug;
  const map = new Map<string, { name: string; cameras: CameraPageData[] }>();
  for (const cam of getAllCamerasForStaticPages()) {
    const slug = toBrandSlug(cam.manufacturer);
    if (!slug) continue;
    let entry = map.get(slug);
    if (!entry) {
      entry = { name: cam.manufacturer, cameras: [] };
      map.set(slug, entry);
    }
    entry.cameras.push(cam);
  }
  // Stable order inside each brand so the rendered HTML — and therefore the
  // prerendered file — is byte-identical between builds.
  for (const entry of map.values()) {
    entry.cameras.sort((a, b) => a.model.localeCompare(b.model));
  }
  _bySlug = map;
  return map;
}

/** Every brand that qualifies for a page, largest catalogue first. */
export function getAllBrands(): BrandSummary[] {
  return [...index().entries()]
    .filter(([, e]) => e.cameras.length >= MIN_CAMERAS_FOR_BRAND_PAGE)
    .map(([slug, e]) => ({ slug, name: e.name, cameraCount: e.cameras.length }))
    .sort((a, b) => b.cameraCount - a.cameraCount || a.name.localeCompare(b.name));
}

/** The brand behind a URL segment, or null when it has no page. */
export function getBrandBySlug(
  slug: string,
): { slug: string; name: string; cameras: CameraPageData[] } | null {
  const entry = index().get(slug);
  if (!entry || entry.cameras.length < MIN_CAMERAS_FOR_BRAND_PAGE) return null;
  return { slug, name: entry.name, cameras: entry.cameras };
}

/**
 * Path to a manufacturer's hub page, or null when that brand has no hub.
 *
 * Camera pages link "up" to their brand through this. Measured 2026-08-17:
 * every camera page is now reachable (0 orphans, down from 21,671), but the
 * graph only flowed downward — `/cameras` → hub → 250 models, with no edge
 * back. The hubs distribute all 21,795 links and received none, and each
 * camera page rendered its manufacturer as a dead `<span>` beside a
 * BreadcrumbList whose middle entry had no `item` at all.
 *
 * Returns null rather than a guessed path because a brand under
 * `MIN_CAMERAS_FOR_BRAND_PAGE` has no hub: linking there would 301 or 404,
 * which is worse than the missing link it replaces. Callers must handle null
 * and fall back to plain text. Centralised here so the existence rule and the
 * threshold can never drift apart between call sites.
 */
export function getBrandHubPath(manufacturer: string): string | null {
  const slug = toBrandSlug(manufacturer);
  if (!slug) return null;
  return getBrandBySlug(slug) ? brandHubPath(slug) : null;
}

/**
 * Models listed per hub page.
 *
 * Hikvision alone has 3,270 models. Putting them all on one page would work,
 * but it spreads that page's link equity across 3,270 anchors and produces a
 * document far larger than anything else on the site. Paginating keeps each
 * page's outgoing links in a normal range while still giving every model a
 * crawlable anchor.
 */
export const BRAND_PAGE_SIZE = 250;

/** Number of hub pages for a brand, always at least 1. */
export function brandPageCount(cameraCount: number): number {
  return Math.max(1, Math.ceil(cameraCount / BRAND_PAGE_SIZE));
}

/**
 * One slice of a brand's models.
 *
 * `page` is 1-based; page 1 lives at `/<slug>` and the rest at
 * `/<slug>/page/<n>`. Out-of-range pages return null so the route can 404
 * rather than render an empty list.
 */
export function getBrandPage(
  slug: string,
  page = 1,
): { slug: string; name: string; cameras: CameraPageData[]; page: number; pageCount: number; total: number } | null {
  const brand = getBrandBySlug(slug);
  if (!brand) return null;
  const pageCount = brandPageCount(brand.cameras.length);
  if (!Number.isInteger(page) || page < 1 || page > pageCount) return null;
  const start = (page - 1) * BRAND_PAGE_SIZE;
  return {
    slug,
    name: brand.name,
    cameras: brand.cameras.slice(start, start + BRAND_PAGE_SIZE),
    page,
    pageCount,
    total: brand.cameras.length,
  };
}

/**
 * SSG paths for page 1 of each hub, i.e. the `brand/:slug` route.
 *
 * Kept separate from the paginated paths because they belong to two different
 * routes: `brand/hikvision/2` has three segments and will never match the
 * two-segment `brand/:slug` pattern, so feeding both lists to one route leaves
 * every page past the first unprerendered.
 */
export function getBrandRootSlugs(): string[] {
  return getAllBrands().map(b => brandHubPath(b.slug).slice(1));
}

/** SSG paths for pages 2..n, i.e. the `brand/:slug/:page` route. */
export function getBrandPagedSlugs(): string[] {
  const paths: string[] = [];
  for (const b of getAllBrands()) {
    for (let p = 2; p <= brandPageCount(b.cameraCount); p++) {
      paths.push(brandPagePath(b.slug, p).slice(1));
    }
  }
  return paths;
}

/** Every hub path, both routes together. Used by the sitemap. */
export function getAllBrandSlugs(): string[] {
  return [...getBrandRootSlugs(), ...getBrandPagedSlugs()];
}
